import api from './api'

// Types
export interface PlatformAgent {
  id: string
  isPlatformAgent: boolean
  platformAgentSince: string
  name: string
  tradingName?: string
  document: string
  email?: string
  phone?: string
  isActive: boolean
  tenantId: string
  tenantName: string
  approvedByName?: string
  userCount: number
}

export interface PlatformAgentCandidate {
  id: string
  name: string
  tradingName?: string
  document: string
  email?: string
  isActive: boolean
  tenantId: string
  tenantName: string
}

// API calls

/**
 * List all platform agents
 */
export async function getPlatformAgents(): Promise<PlatformAgent[]> {
  const response = await api.get('/master/platform-agents')
  return response.data.platformAgents
}

/**
 * List candidates for platform agent status
 */
export async function getPlatformAgentCandidates(params?: {
  tenantId?: string
  search?: string
}): Promise<PlatformAgentCandidate[]> {
  const queryParams = new URLSearchParams()
  if (params?.tenantId) queryParams.append('tenantId', params.tenantId)
  if (params?.search) queryParams.append('search', params.search)

  const queryString = queryParams.toString()
  const response = await api.get(
    `/master/platform-agents/candidates${queryString ? `?${queryString}` : ''}`
  )
  return response.data.candidates
}

/**
 * Set a freight forwarder as platform agent
 */
export async function setPlatformAgent(freightForwarderId: string): Promise<void> {
  await api.post(`/master/platform-agents/${freightForwarderId}`)
}

/**
 * Remove platform agent status
 */
export async function removePlatformAgent(freightForwarderId: string): Promise<void> {
  await api.delete(`/master/platform-agents/${freightForwarderId}`)
}

export interface CreatePlatformAgentRequest {
  email: string
  companyName: string
  document?: string
  contactName?: string
  phone?: string
}

/**
 * Create a new freight forwarder and set as platform agent
 */
export async function createPlatformAgent(data: CreatePlatformAgentRequest): Promise<void> {
  await api.post('/master/platform-agents/create', data)
}

/**
 * Resend invite email to a platform agent (existing agent without users)
 */
export async function resendPlatformAgentInvite(agentId: string): Promise<{ email: string }> {
  const response = await api.post(`/master/platform-agents/${agentId}/resend-invite`)
  return response.data
}

// ============================================================
// PENDING PLATFORM INVITES
// ============================================================

export interface PendingPlatformInvite {
  id: string
  email: string
  companyName: string
  contactName?: string
  phone?: string
  expiresAt: string
  createdAt: string
  invitedByName?: string
  tenantName?: string
}

/**
 * List pending platform agent invites
 */
export async function getPendingPlatformInvites(): Promise<PendingPlatformInvite[]> {
  const response = await api.get('/master/platform-agents/pending-invites')
  return response.data.invites
}

/**
 * Resend a pending platform invite
 */
export async function resendPlatformInvite(inviteId: string): Promise<{ email: string }> {
  const response = await api.post(`/master/platform-agents/invites/${inviteId}/resend`)
  return response.data
}

/**
 * Cancel a pending platform invite
 */
export async function cancelPlatformInvite(inviteId: string): Promise<void> {
  await api.delete(`/master/platform-agents/invites/${inviteId}`)
}

// ============================================================
// PLATFORM AGENT EXCLUSIONS
// ============================================================

export interface ShipperExclusion {
  id: string
  shipperId: string
  shipperName: string
  shipperTradingName?: string
  shipperDocument?: string
  reason?: string
  excludedAt: string
  excludedByName?: string
}

export interface AvailableShipperForExclusion {
  id: string
  name: string
  tradingName?: string
  document?: string
}

/**
 * List all shippers excluded from a platform agent
 */
export async function getExclusions(agentId: string): Promise<ShipperExclusion[]> {
  const response = await api.get(`/master/platform-agents/${agentId}/exclusions`)
  return response.data.exclusions
}

/**
 * Add a shipper to the exclusion list
 */
export async function addExclusion(
  agentId: string,
  shipperId: string,
  reason?: string
): Promise<ShipperExclusion> {
  const response = await api.post(`/master/platform-agents/${agentId}/exclusions`, {
    shipperId,
    reason,
  })
  return response.data.exclusion
}

/**
 * Remove a shipper from the exclusion list
 */
export async function removeExclusion(agentId: string, shipperId: string): Promise<void> {
  await api.delete(`/master/platform-agents/${agentId}/exclusions/${shipperId}`)
}

/**
 * List shippers available to be added to exclusion list
 */
export async function getAvailableShippersForExclusion(
  agentId: string
): Promise<AvailableShipperForExclusion[]> {
  const response = await api.get(`/master/platform-agents/${agentId}/available-shippers`)
  return response.data.shippers
}

// ============================================================
// PLATFORM AGENT FEES
// ============================================================

export interface PlatformAgentFees {
  feeDesconsolidacao: number
  feeLogistic: number
}

/**
 * Get platform agent fee configuration
 */
export async function getPlatformAgentFees(agentId: string): Promise<{
  fees: PlatformAgentFees
  agentName: string
}> {
  const response = await api.get(`/master/platform-agents/${agentId}/fees`)
  return response.data
}

/**
 * Update platform agent fee configuration
 */
export async function updatePlatformAgentFees(
  agentId: string,
  fees: Partial<PlatformAgentFees>
): Promise<{
  fees: PlatformAgentFees
  agentName: string
}> {
  const response = await api.put(`/master/platform-agents/${agentId}/fees`, fees)
  return response.data
}
